import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Clock, CheckCircle, AlertCircle, MessageSquare,
  Filter, RefreshCw, Shield, LogOut, ChevronRight
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';
import QuoteCard from '../../components/QuoteCard';
import { useNotification } from '../../contexts/NotificationContext';

const AdminQuotes = () => {
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const { showModal } = useNotification();

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [saving, setSaving] = useState(null); // Track which quote is being saved

  // Check admin role and load quotes
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/auth/login');
        return;
      }
      checkAdminRole();
    }
  }, [user, authLoading, navigate]);

  const checkAdminRole = async () => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error checking role:', error);
        setUserRole('customer');
        return;
      }

      const role = profile?.role || 'customer';
      setUserRole(role);

      if (role === 'admin' || role === 'editor') {
        loadQuotes();
      }
    } catch (err) {
      console.error('Role check error:', err);
      setUserRole('customer');
    }
  };

  const loadQuotes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('quote_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes(data || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    setSaving(quoteId);
    try {
      const { error } = await supabase
        .from('quote_requests')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', quoteId);

      if (error) throw error;

      // Update local state
      setQuotes(prev => prev.map(q =>
        q.id === quoteId ? { ...q, status: newStatus } : q
      ));
    } catch (error) {
      console.error('Error updating status:', error);
      showModal(
        language === 'tr' ? 'Hata' : 'Error',
        language === 'tr' ? 'Durum güncellenemedi' : 'Failed to update status',
        'error'
      );
    } finally {
      setSaving(null);
    }
  };

  const handleAdminNotesChange = async (quoteId, notes) => {
    setSaving(quoteId);
    try {
      const { error } = await supabase
        .from('quote_requests')
        .update({
          admin_notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', quoteId);

      if (error) throw error;

      // Update local state
      setQuotes(prev => prev.map(q =>
        q.id === quoteId ? { ...q, admin_notes: notes } : q
      ));
    } catch (error) {
      console.error('Error updating admin notes:', error);
      showModal(
        language === 'tr' ? 'Hata' : 'Error',
        language === 'tr' ? 'Notlar kaydedilemedi' : 'Failed to save notes',
        'error'
      );
    } finally {
      setSaving(null);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Filter quotes by status
  const filteredQuotes = statusFilter === 'all'
    ? quotes
    : quotes.filter(q => q.status === statusFilter);

  // Stats
  const stats = [
    {
      label: language === 'tr' ? 'Toplam' : 'Total',
      value: quotes.length,
      icon: FileText,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      label: language === 'tr' ? 'Yeni' : 'New',
      value: quotes.filter(q => q.status === 'new').length,
      icon: Clock,
      color: 'from-blue-500 to-blue-600'
    },
    {
      label: language === 'tr' ? 'İnceleniyor' : 'In Review',
      value: quotes.filter(q => q.status === 'in_review').length,
      icon: AlertCircle,
      color: 'from-yellow-500 to-orange-500'
    },
    {
      label: language === 'tr' ? 'Yanıtlandı' : 'Responded',
      value: quotes.filter(q => q.status === 'responded').length,
      icon: MessageSquare,
      color: 'from-green-500 to-emerald-500'
    },
    {
      label: language === 'tr' ? 'Kapatıldı' : 'Closed',
      value: quotes.filter(q => q.status === 'closed').length,
      icon: CheckCircle,
      color: 'from-gray-500 to-gray-600'
    }
  ];

  // Loading state
  if (authLoading || (user && userRole === null)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'tr' ? 'Yükleniyor...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // Access denied
  if (userRole && userRole !== 'admin' && userRole !== 'editor') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {language === 'tr' ? 'Erişim Reddedildi' : 'Access Denied'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {language === 'tr'
              ? 'Bu sayfayı görüntülemek için yönetici yetkisi gereklidir.'
              : 'Administrator privileges are required to view this page.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
          >
            {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400">
              {language === 'tr' ? 'Ana Sayfa' : 'Home'}
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/admin" className="hover:text-blue-600 dark:hover:text-blue-400">
              Admin
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 dark:text-white font-medium">
              {language === 'tr' ? 'Teklif Talepleri' : 'Quote Requests'}
            </span>
          </nav>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {language === 'tr' ? 'Teklif Talepleri' : 'Quote Requests'}
              </h1>
            </div>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {language === 'tr' ? 'Çıkış' : 'Sign Out'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Filters & Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="status-filter"
            >
              <option value="all">{language === 'tr' ? 'Tüm Durumlar' : 'All Statuses'}</option>
              <option value="new">{language === 'tr' ? 'Yeni' : 'New'}</option>
              <option value="in_review">{language === 'tr' ? 'İnceleniyor' : 'In Review'}</option>
              <option value="responded">{language === 'tr' ? 'Yanıtlandı' : 'Responded'}</option>
              <option value="closed">{language === 'tr' ? 'Kapatıldı' : 'Closed'}</option>
            </select>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {filteredQuotes.length} {language === 'tr' ? 'talep' : 'requests'}
            </span>
          </div>
          <button
            onClick={loadQuotes}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {language === 'tr' ? 'Yenile' : 'Refresh'}
          </button>
        </div>

        {/* Quotes List */}
        {loading ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">
              {language === 'tr' ? 'Talepler yükleniyor...' : 'Loading requests...'}
            </p>
          </div>
        ) : filteredQuotes.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {statusFilter === 'all'
                ? (language === 'tr' ? 'Henüz talep yok' : 'No requests yet')
                : (language === 'tr' ? 'Bu durumda talep yok' : 'No requests with this status')}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredQuotes.map((quote) => (
              <AdminQuoteCard
                key={quote.id}
                quote={quote}
                language={language}
                onStatusChange={handleStatusChange}
                onAdminNotesChange={handleAdminNotesChange}
                saving={saving === quote.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Extended QuoteCard wrapper with admin notes editing
const AdminQuoteCard = ({ quote, language, onStatusChange, onAdminNotesChange, saving }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(quote.admin_notes || '');

  const handleSaveNotes = () => {
    onAdminNotesChange(quote.id, notesValue);
    setEditingNotes(false);
  };

  // Status configuration
  const statusConfig = {
    new: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      icon: Clock,
      labelTR: 'Yeni',
      labelEN: 'New'
    },
    in_review: {
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      icon: AlertCircle,
      labelTR: 'İnceleniyor',
      labelEN: 'In Review'
    },
    responded: {
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      icon: MessageSquare,
      labelTR: 'Yanıtlandı',
      labelEN: 'Responded'
    },
    closed: {
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
      icon: CheckCircle,
      labelTR: 'Kapatıldı',
      labelEN: 'Closed'
    }
  };

  const status = statusConfig[quote.status] || statusConfig.new;
  const StatusIcon = status.icon;

  // Service labels
  const serviceLabels = {
    'ecommerce': { tr: 'E-Ticaret & Marketplace', en: 'E-Commerce & Marketplace' },
    'web-app': { tr: 'Web & Uygulama Geliştirme', en: 'Web & App Development' },
    'automation': { tr: 'Otomasyon & Entegrasyon', en: 'Automation & Integration' },
    'ai': { tr: 'AI Ajanları & Chatbotlar', en: 'AI Agents & Chatbots' },
    '3d': { tr: '3D Modelleme & Baskı', en: '3D Modeling & Printing' },
    'marketing': { tr: 'Performans Pazarlama', en: 'Performance Marketing' }
  };

  const budgetLabels = {
    '5k-10k': '$5,000 - $10,000',
    '10k-25k': '$10,000 - $25,000',
    '25k-50k': '$25,000 - $50,000',
    '50k+': '$50,000+',
    'flexible': language === 'tr' ? 'Esnek / Teklife Göre' : 'Flexible / Quote-Based'
  };

  const serviceName = serviceLabels[quote.service_type]?.[language === 'tr' ? 'tr' : 'en'] ||
    quote.service_type ||
    (language === 'tr' ? 'Genel Talep' : 'General Request');

  const maxPreviewLength = 150;
  const hasLongDescription = quote.description && quote.description.length > maxPreviewLength;
  const previewDescription = hasLongDescription
    ? quote.description.substring(0, maxPreviewLength) + '...'
    : quote.description;

  return (
    <motion.div
      layout
      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
    >
      {/* Card Header */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3 gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                {serviceName}
              </h3>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {language === 'tr' ? status.labelTR : status.labelEN}
              </span>
              {saving && (
                <span className="text-xs text-blue-600 dark:text-blue-400 animate-pulse">
                  {language === 'tr' ? 'Kaydediliyor...' : 'Saving...'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
              <span>{quote.full_name}</span>
              <span>•</span>
              <a href={`mailto:${quote.email}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                {quote.email}
              </a>
              {quote.phone && (
                <>
                  <span>•</span>
                  <a href={`tel:${quote.phone}`} className="hover:underline">{quote.phone}</a>
                </>
              )}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {new Date(quote.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>

          {/* Status Dropdown */}
          <select
            value={quote.status}
            onChange={(e) => onStatusChange(quote.id, e.target.value)}
            disabled={saving}
            className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
            data-testid="quote-status-select"
          >
            <option value="new">{language === 'tr' ? 'Yeni' : 'New'}</option>
            <option value="in_review">{language === 'tr' ? 'İnceleniyor' : 'In Review'}</option>
            <option value="responded">{language === 'tr' ? 'Yanıtlandı' : 'Responded'}</option>
            <option value="closed">{language === 'tr' ? 'Kapatıldı' : 'Closed'}</option>
          </select>
        </div>

        {/* Description Preview */}
        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
          {isExpanded ? quote.description : previewDescription}
        </p>

        {/* Expand/Collapse */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-4 inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
        >
          {isExpanded ? (
            <>{language === 'tr' ? 'Daha Az Göster' : 'Show Less'}</>
          ) : (
            <>{language === 'tr' ? 'Daha Fazlasını Göster' : 'Show More'}</>
          )}
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="px-6 pb-6 pt-2 border-t border-gray-100 dark:border-gray-800"
        >
          {/* Details Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {quote.budget_range && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                  {language === 'tr' ? 'Bütçe' : 'Budget'}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {budgetLabels[quote.budget_range] || quote.budget_range}
                </p>
              </div>
            )}
            {quote.target_date && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                  {language === 'tr' ? 'Hedef Tarih' : 'Target Date'}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {new Date(quote.target_date).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')}
                </p>
              </div>
            )}
            {quote.company_name && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                  {language === 'tr' ? 'Şirket' : 'Company'}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {quote.company_name}
                </p>
              </div>
            )}
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                {language === 'tr' ? 'Son Güncelleme' : 'Last Updated'}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {new Date(quote.updated_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')}
              </p>
            </div>
          </div>

          {/* Admin Notes Section */}
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {language === 'tr' ? 'İç Notlar (Sadece Admin)' : 'Internal Notes (Admin Only)'}
              </label>
              {!editingNotes && (
                <button
                  onClick={() => setEditingNotes(true)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {language === 'tr' ? 'Düzenle' : 'Edit'}
                </button>
              )}
            </div>

            {editingNotes ? (
              <div className="space-y-2">
                <textarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder={language === 'tr' ? 'İç notlar ekleyin...' : 'Add internal notes...'}
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveNotes}
                    disabled={saving}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (language === 'tr' ? 'Kaydet' : 'Save')}
                  </button>
                  <button
                    onClick={() => {
                      setEditingNotes(false);
                      setNotesValue(quote.admin_notes || '');
                    }}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    {language === 'tr' ? 'İptal' : 'Cancel'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 min-h-[60px]">
                {quote.admin_notes ? (
                  <p className="text-sm text-amber-800 dark:text-amber-200 whitespace-pre-wrap">{quote.admin_notes}</p>
                ) : (
                  <p className="text-sm text-amber-600 dark:text-amber-400 italic">
                    {language === 'tr' ? 'Henüz not eklenmemiş' : 'No notes added yet'}
                  </p>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default AdminQuotes;
